/**
 * Document Type Utilities
 * 
 * Provides helper functions for working with document types,
 * including identifying audio types dynamically from the API.
 * 
 * NOTE: These functions now prefer DataContext cached types but fall back
 * to direct API calls for backwards compatibility.
 */

import { DocumentTypeApi } from '../services/ApiService';

let cachedDocumentTypes = null;
let cacheTimestamp = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetches and caches all document types from the API.
 * DEPRECATED: Prefer using DataContext.documentTypes directly in components.
 * This function is kept for backwards compatibility and standalone utility usage.
 * @param {Array} contextTypes - Optional pre-fetched types from DataContext
 * @returns {Promise<Array>} Array of document type objects
 */
export async function getDocumentTypes(contextTypes = null) {
  // If types provided from context, use them directly (no caching needed)
  if (contextTypes && Array.isArray(contextTypes) && contextTypes.length > 0) {
    console.log('[documentTypeUtils] Using types from DataContext:', contextTypes.length);
    return contextTypes;
  }

  const now = Date.now();
  
  // Return cached data if still fresh
  if (cachedDocumentTypes && cacheTimestamp && (now - cacheTimestamp) < CACHE_TTL) {
    console.log('[documentTypeUtils] Using locally cached types:', cachedDocumentTypes.length);
    return cachedDocumentTypes;
  }
  
  try {
    console.log('[documentTypeUtils] Fetching document types from API (fallback)...');
    const response = await DocumentTypeApi.getDocumentTypes();
    if (!response.ok) {
      throw new Error(`Failed to fetch document types: ${response.status}`);
    }
    
    const data = await response.json();
    cachedDocumentTypes = Array.isArray(data) ? data : [];
    cacheTimestamp = now;
    
    console.log('[documentTypeUtils] Fetched and cached', cachedDocumentTypes.length, 'types');
    return cachedDocumentTypes;
  } catch (error) {
    console.error('[documentTypeUtils] Failed to fetch document types:', error);
    // Return cached data even if stale, or empty array
    return cachedDocumentTypes || [];
  }
}

/**
 * Gets the IDs of all audio document types (types with names starting with "audio/")
 * @param {Array} contextTypes - Optional pre-fetched types from DataContext
 * @returns {Promise<Array<number>>} Array of audio document type IDs
 */
export async function getAudioDocumentTypeIds(contextTypes = null) {
  const types = await getDocumentTypes(contextTypes);
  return types
    .filter(type => type.name && type.name.toLowerCase().startsWith('audio/'))
    .map(type => type.id)
    .filter(id => id != null);
}

/**
 * Clears the document types cache (useful after admin updates)
 */
export function clearDocumentTypesCache() {
  cachedDocumentTypes = null;
  cacheTimestamp = null;
}
